import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  FieldArray,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  min2CharValidator,
  requiredValidator,
} from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import FormImageUpload from "../../components/formFields/FormImageUpload";
import {
  ActivityImageUpload,
  createActivity,
  getActivityByID,
  TimelineImagesUpload,
  updateActivity,
} from "./services/activities.services";
import { clearActivityDetails } from "./activitiesSlice";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import { getFile, slugify } from "../../_helper/helper";
import FormCheckbox from "../../components/formFields/FormCheckbox";
import ActivityTimelineArray from "./ActivityTimelineArray";
import FormTextArea from "../../components/formFields/FormTextArea";

const CreateActivity: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const ActivityID = location.state?.ActivityID;
  const ActivityDetail = useAppSelector(
    (state) => state.activity.ActivityDetail,
  );
  const loading = useAppSelector((state) => state.activity.loading);
  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [ActivityDetail]);

  useEffect(() => {
    if (ActivityID) {
      dispatch(getActivityByID(ActivityID));
    }
    return () => {
      dispatch(clearActivityDetails());
    };
  }, [ActivityID]);

  const handleSubmit = async (values: any) => {
    const insertPayload = {
      ActivityName: values.ActivityName ? values.ActivityName : "",
      ActivityURL: values.ActivityURL ? values.ActivityURL : "",
      Duration: values.Duration,
      DifficultyType: values.DifficultyType ? values.DifficultyType : "",
      Availability: values.Availability ? values.Availability : "",
      Description: values.Description ? values.Description : "",
      ShortDescription: values.ShortDescription ? values.ShortDescription : "",
      WhatsInclude: values.WhatsInclude ? values.WhatsInclude : "",
      ImportantTips: values.ImportantTips ? values.ImportantTips : "",
      MetaKeywords: values.MetaKeywords ? values.MetaKeywords : "",
      MetaTitle: values.MetaTitle ? values.MetaTitle : "",
      MetaDescription: values.MetaDescription ? values.MetaDescription : "",
      IsShowOnHome: values.IsShowOnHome ? values.IsShowOnHome : false,
      MainImage: values.MainImage ? values.MainImage : "",
      BannerImage: values.BannerImage ? values.BannerImage : "",
      ActivityTimeline: values.ActivityTimeline
        ? values.ActivityTimeline?.map((e: any) => ({
            ID: e?.ID,
            Title: e?.Title,
            Time: e?.Time,
            Description: e?.Description,
          }))
        : [],
      IsActive: values.IsActive === 1,
    };

    const MainImage =
      values?.MainImage && typeof values?.MainImage !== "string"
        ? getFile(values?.MainImage)
        : "";
    const BannerImage =
      values?.BannerImage && typeof values?.BannerImage !== "string"
        ? getFile(values.BannerImage)
        : "";

    try {
      let activityId = ActivityID;
      let savedTimelineResponse: any = null;

      if (ActivityID) {
        // For update, we need to include IDs for existing items
        const editPayload = {
          ...insertPayload,
          ID: ActivityID,
        };

        const response = await dispatch(updateActivity(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          savedTimelineResponse = response;
          activityId = ActivityID;

          // Upload main and banner images
          await uploadMainAndBannerImages(
            values,
            activityId,
            MainImage,
            BannerImage,
          );

          // Handle timeline images upload with the response data
          await handleTimelineImagesUpload(
            values,
            activityId,
            savedTimelineResponse,
          );

          navigate("/activities");
        }
      } else {
        // Create new activity
        const response = await dispatch(createActivity(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          savedTimelineResponse = response;
          activityId = response?.payload?.Data?.ID;

          if (activityId) {
            // Upload main and banner images
            await uploadMainAndBannerImages(
              values,
              activityId,
              MainImage,
              BannerImage,
            );

            // Handle timeline images upload with the response data
            await handleTimelineImagesUpload(
              values,
              activityId,
              savedTimelineResponse,
            );

            navigate("/activities");
          }
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  // Helper function for main and banner images
  const uploadMainAndBannerImages = async (
    values: any,
    activityId: string,
    MainImage: any,
    BannerImage: any,
  ) => {
    if (
      (values?.MainImage && typeof values?.MainImage !== "string") ||
      (values?.BannerImage && typeof values?.BannerImage !== "string")
    ) {
      const mainImageData = new FormData();
      if (values?.MainImage && typeof values?.MainImage !== "string") {
        mainImageData.append("MainImage", MainImage);
      }
      if (values?.BannerImage && typeof values?.BannerImage !== "string") {
        mainImageData.append("BannerImage", BannerImage);
      }
      mainImageData.append("ID", activityId);
      await dispatch(ActivityImageUpload(mainImageData));
    }
  };

  // Helper function for timeline images with proper ID mapping
  const handleTimelineImagesUpload = async (
    values: any,
    activityId: string,
    response: any,
  ) => {
    if (!values.ActivityTimeline || values.ActivityTimeline.length === 0) {
      return;
    }

    // Get the saved timeline IDs from response (it's a string like "1,2,3,5,6,8")
    const savedTimelineIdsString =
      response?.payload?.Data?.ActivityTimelineIDs || "";

    if (!savedTimelineIdsString) {
      return;
    }

    // Convert the string to array of IDs
    const savedTimelineIds = savedTimelineIdsString
      .split(",")
      .map((id: string) => parseInt(id.trim()))
      .filter((id: number) => !isNaN(id));

    // Find which timeline items have new images
    const timelineItemsWithNewImages = values.ActivityTimeline.map(
      (item: any, index: number) => ({
        ...item,
        clientIndex: index,
        hasNewImage:
          item?.TimelineImage && typeof item?.TimelineImage !== "string",
      }),
    ).filter((item: any) => item.hasNewImage);

    if (timelineItemsWithNewImages.length === 0) {
      return;
    }

    // Now we need to map client items to server IDs
    const timelineToUpload: any = [];

    if (ActivityID) {
      // For UPDATE operation
      // We need to understand the transformation that happened on server:
      // 1. Items with IDs that still exist (were not deleted)
      // 2. New items without IDs that got new IDs

      // Strategy:
      // 1. First, handle existing items (with IDs) - they keep their IDs
      // 2. Then, new items (without IDs) get IDs from the end of savedTimelineIds

      // Separate existing items and new items
      const existingItems = timelineItemsWithNewImages.filter(
        (item: any) => item.ID,
      );
      const newItems = timelineItemsWithNewImages.filter(
        (item: any) => !item.ID,
      );

      // For existing items: find their ID in savedTimelineIds
      existingItems.forEach((item: any) => {
        // The ID should be in savedTimelineIds if it wasn't deleted
        if (savedTimelineIds.includes(parseInt(item.ID))) {
          timelineToUpload.push({
            id: parseInt(item.ID),
            file: getFile(item.TimelineImage),
          });
        }
      });

      // For new items: they get IDs from savedTimelineIds that are NOT in the original items
      // Get all original existing IDs
      const originalExistingIds = values.ActivityTimeline.filter(
        (item: any) => item.ID,
      )
        .map((item: any) => parseInt(item.ID))
        .filter((id: number) => !isNaN(id));

      // Find which IDs in savedTimelineIds are NEW (not in original)
      const newIdsFromServer = savedTimelineIds.filter(
        (id: number) => !originalExistingIds.includes(id),
      );

      // Map new items to new IDs (assuming order is preserved)
      newItems.forEach((item: any, index: number) => {
        if (index < newIdsFromServer.length) {
          timelineToUpload.push({
            id: newIdsFromServer[index],
            file: getFile(item.TimelineImage),
          });
        }
      });
    } else {
      // For CREATE operation: All items are new
      // savedTimelineIds contains all the new IDs in order
      timelineItemsWithNewImages.forEach((item: any, index: number) => {
        // Match by index (assuming order is preserved)
        if (index < savedTimelineIds.length) {
          timelineToUpload.push({
            id: savedTimelineIds[index],
            file: getFile(item.TimelineImage),
          });
        }
      });
    }

    if (timelineToUpload.length === 0) {
      return;
    }

    // Create FormData for the timeline images
    const timelineImageData = new FormData();
    timelineImageData.append("ActivityID", activityId.toString());

    // Create comma-separated IDs string
    const idsString = timelineToUpload.map((item: any) => item.id).join(",");
    timelineImageData.append("IDs", idsString);

    // Append all image files
    timelineToUpload.forEach((item: any) => {
      if (item.file) {
        timelineImageData.append("attachment", item.file);
      }
    });

    console.log("Uploading timeline images with IDs:", idsString);

    // Call the timeline images API
    await dispatch(TimelineImagesUpload(timelineImageData));
  };

  // const handleSubmit = async (values: any) => {
  //   const insertPayload = {
  //     ActivityName: values.ActivityName ? values.ActivityName : "",
  //     ActivityURL: values.ActivityURL ? values.ActivityURL : "",
  //     Duration: values.Duration,
  //     DifficultyType: values.DifficultyType ? values.DifficultyType : "",
  //     Availability: values.Availability ? values.Availability : "",
  //     Description: values.Description ? values.Description : "",
  //     ShortDescription: values.ShortDescription ? values.ShortDescription : "",
  //     WhatsInclude: values.WhatsInclude ? values.WhatsInclude : "",
  //     ImportantTips: values.ImportantTips ? values.ImportantTips : "",
  //     IsShowOnHome: values.IsShowOnHome ? values.IsShowOnHome : false,
  //     MainImage: values.MainImage ? values.MainImage : "",
  //     BannerImage: values.BannerImage ? values.BannerImage : "",
  //     ActivityTimeline: values.ActivityTimeline
  //       ? values.ActivityTimeline?.map((e: any) => ({
  //           ID: e?.ID,
  //           Title: e?.Title,
  //           Time: e?.Time,
  //           Description: e?.Description,
  //         }))
  //       : [],
  //     IsActive: values.IsActive === 1,
  //   };
  //   const ImageData = new FormData();
  //   const MainImage =
  //     values?.MainImage && typeof values?.MainImage !== "string"
  //       ? getFile(values?.MainImage)
  //       : "";
  //   const BannerImage =
  //     values?.BannerImage && typeof values?.BannerImage !== "string"
  //       ? getFile(values.BannerImage)
  //       : "";

  //   try {
  //     if (ActivityID) {
  //       const editPayload = {
  //         ...insertPayload,
  //         ID: ActivityID,
  //       };

  //       const response = await dispatch(updateActivity(editPayload));
  //       if (response?.meta?.requestStatus === "fulfilled") {
  //         if (
  //           (values?.MainImage && typeof values?.MainImage !== "string") ||
  //           (values?.BannerImage && typeof values?.BannerImage !== "string")
  //         ) {
  //           const Images = values.Images?.map((fileItem: any) =>
  //             fileItem.getRawFile(),
  //           );

  //           Images?.map((image: any) => ImageData.append("Image", image));
  //           // ImageData.append("ID", ActivityID);

  //           ImageData.append("MainImage", MainImage);
  //           ImageData.append("BannerImage", BannerImage);
  //           ImageData.append("ID", ActivityID);
  //           const imageResponse = await dispatch(
  //             ActivityImageUpload(ImageData),
  //           );
  //           if (imageResponse?.meta?.requestStatus === "fulfilled") {
  //             navigate("/activities");
  //           } else {
  //             navigate("/activities");
  //           }
  //         } else {
  //           navigate("/activities");
  //         }
  //       }
  //     } else {
  //       const response = await dispatch(createActivity(insertPayload));
  //       if (response?.meta?.requestStatus === "fulfilled") {
  //         if (
  //           ((values?.MainImage && typeof values?.MainImage !== "string") ||
  //             (values?.BannerImage &&
  //               typeof values?.BannerImage !== "string")) &&
  //           response?.payload?.Data?.ID
  //         ) {
  //           const Images = values.Images?.map((fileItem: any) =>
  //             fileItem.getRawFile(),
  //           );

  //           Images?.map((image: any) => ImageData.append("Image", image));
  //           // ImageData.append("ID", response?.payload?.Data?.ID);

  //           ImageData.append("MainImage", MainImage);
  //           ImageData.append("BannerImage", BannerImage);
  //           ImageData.append("ID", response?.payload?.Data?.ID);
  //           const imageResponse = await dispatch(
  //             ActivityImageUpload(ImageData),
  //           );
  //           if (imageResponse?.meta?.requestStatus === "fulfilled") {
  //             navigate("/activities");
  //           } else {
  //             navigate("/activities");
  //           }
  //         } else {
  //           navigate("/activities");
  //         }
  //       }
  //     }
  //   } catch (error) {
  //     console.error("Error in handleSubmit:", error);
  //     throw error;
  //   }
  // };

  const handleCreateSlug = (e: any, formRenderProps: FormRenderProps) => {
    const slug = slugify(e.value);
    formRenderProps.onChange("ActivityURL", { value: slug });
  };

  // if (loading) return <LoadingPanel gridRef={gridRef} />;
  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <div>
        <Form
          onSubmit={handleSubmit}
          key={formKey}
          initialValues={ActivityDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <ShadowCard style={{ padding: 12 }}>
                <GridLayout
                  style={{ marginRight: 30 }}
                  gap={{ rows: 5, cols: 10 }}
                  cols={[
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                  ]}
                >
                  <GridLayoutItem colSpan={3}>
                    <Typography.h4>
                      {ActivityID ? "Update Activity" : "Add Activity"}
                    </Typography.h4>
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="ActivityName"
                      label="Activity Name"
                      placeholder="Activity Name"
                      onChange={(e) => handleCreateSlug(e, formRenderProps)}
                      component={FormTextField}
                      validator={min2CharValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="ActivityURL"
                      label="Activity URL"
                      placeholder="Activity URL"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="Duration"
                      label="Duration"
                      placeholder="Duration"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`DifficultyType`}
                      label="Difficulty Type"
                      placeholder="Easy, Moderate, Hard"
                      component={FormSelectionField}
                      options={[
                        {
                          label: "Easy",
                          value: "Easy",
                        },
                        {
                          label: "Moderate",
                          value: "Moderate",
                        },
                        {
                          label: "Hard",
                          value: "Hard",
                        },
                      ]}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`Availability`}
                      label="Availability"
                      placeholder="Easy, Moderate, Hard"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={[
                        {
                          label: "Daily",
                          value: "Daily",
                        },
                        {
                          label: "Seasonal",
                          value: "Seasonal",
                        },
                        {
                          label: "Specific Days",
                          value: "Specific Days",
                        },
                      ]}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    style={{
                      width: "100%",
                      margin: "18px 0px 0px",
                      padding: "0px 10px 8px",
                      fontWeight: 700,
                      background: "whitesmoke",
                      borderRadius: 5,
                      height: 48,
                    }}
                  >
                    <Field
                      name="IsShowOnHome"
                      label="Show At Home"
                      component={FormCheckbox}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4}>
                    <Field
                      name="ShortDescription"
                      label="Short Description"
                      component={FormTextArea}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4}>
                    <Field
                      name="Description"
                      label="Description"
                      component={FormRichTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="WhatsInclude"
                      label="Whats Include"
                      component={FormRichTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="ImportantTips"
                      label="Important Tips"
                      component={FormRichTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem colSpan={4}>
                    <Field
                      name="MetaKeywords"
                      label="Meta Keywords"
                      placeholder="Meta Keywords"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4}>
                    <Field
                      name="MetaTitle"
                      label="Meta Title"
                      placeholder="Meta Title"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4}>
                    <Field
                      name="MetaDescription"
                      label="Meta Description"
                      placeholder="Meta Description"
                      component={FormTextArea}
                      style={{ height: 130 }}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem
                    colSpan={3}
                    style={{
                      marginTop: 20,
                      display: "flex",
                      width: "100%",
                      gap: "10px",
                    }}
                  >
                    <Field
                      justifyContent="start"
                      name={`MainImage`}
                      label="Main Image"
                      type="file"
                      alternateName="Main Image"
                      component={FormImageUpload}
                      validator={requiredValidator}
                      astrike={true}
                    />
                    <Field
                      justifyContent="start"
                      name={`BannerImage`}
                      label="Banner Image"
                      type="file"
                      alternateName="Banner Image"
                      component={FormImageUpload}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem
                    style={{
                      marginTop: 40,
                    }}
                  >
                    <Field
                      name={`IsActive`}
                      label="Status"
                      placeholder="i.e. Active"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={STATUSARRAY?.map((status: any) => {
                        return {
                          value: status?.value,
                          label: status?.label,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={4}
                    className="mt-4 pt-4"
                    style={{
                      borderTop: "1px dashed grey",
                    }}
                  >
                    <FieldArray
                      formRenderProps={formRenderProps}
                      component={ActivityTimelineArray}
                      name="ActivityTimeline"
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={4}
                    style={{
                      marginTop: "20px",
                      textAlign: "end",
                    }}
                  >
                    <div>
                      <ButtonWithLoading
                        label={ActivityID ? "Update" : "Save"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />
                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"primary"}
                        style={{ marginLeft: 10 }}
                        onClick={() => navigate("/activities")}
                      >
                        Cancel
                      </Button>
                    </div>
                  </GridLayoutItem>
                </GridLayout>
              </ShadowCard>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateActivity;
